import { useEffect, useState } from "react";
import { AlertTriangle, Phone, PhoneCall, Sparkles } from "lucide-react";
import { assetUrl } from "../../utils/assetUrl";
import "./NightAccidentGuideOverlay.css";

const accidentGuideSteps = [
  {
    id: "hazard",
    index: 1,
    label: "打开双闪",
    caption: "打开双闪灯",
    image: assetUrl("assets/figma/night-ai/accident-guide/hazard-button.png")
  },
  {
    id: "triangle",
    index: 2,
    label: "取出三角警示牌",
    caption: "打开后备箱，可从后备箱门上或地板处找到三角警示牌",
    image: assetUrl("assets/figma/night-ai/accident-guide/triangle-retrieval.png")
  },
  {
    id: "placement",
    index: 3,
    label: "打开双闪",
    caption: "置于车后 50-100 米处，走路约 70-140 步",
    image: assetUrl("assets/figma/night-ai/accident-guide/triangle-placement.png")
  }
] as const;

const accidentReviewFrames = [
  {
    id: "approach",
    time: "09:23:15",
    title: "接近隧道",
    description: "本车接近 XX 隧道入口，前车在车道线附近行驶，隧道口车流开始压缩。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-01.webp")
  },
  {
    id: "left-vehicle",
    time: "09:23:16",
    title: "左侧车辆切入",
    description: "左侧黑色 SUV 向右偏移，蓝色车辆位于右侧车道，双方横向距离缩短。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-02.webp")
  },
  {
    id: "side-pressure",
    time: "09:23:17",
    title: "入口处并行",
    description: "黑色 SUV 与蓝色车辆在隧道口并行，车辆位置关系进入高风险区域。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-03.webp")
  },
  {
    id: "contact-risk",
    time: "09:23:18",
    title: "疑似侧向挤压",
    description: "蓝色车辆受左侧车辆影响向本车前方靠近，本车前方视野被快速遮挡。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-04.webp")
  },
  {
    id: "forced-merge",
    time: "09:23:19",
    title: "白色车辆切入",
    description: "白色宝马 X1 从左侧进入本车道，切入位置距离本车较近。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-05.webp")
  },
  {
    id: "distance-short",
    time: "09:23:20",
    title: "跟车距离不足",
    description: "白色车辆继续向隧道内行驶，本车与其距离缩短，预留制动空间不足。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-06.webp")
  },
  {
    id: "tunnel-entry",
    time: "09:23:21",
    title: "进入隧道口",
    description: "车辆进入明暗变化区域，识别结果受隧道口光照变化影响。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-07.webp")
  },
  {
    id: "follow-brake",
    time: "09:23:22",
    title: "持续跟随",
    description: "白色车辆占据本车前方，本车处于跟随减速阶段。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-08.webp")
  },
  {
    id: "tunnel-shadow",
    time: "09:23:23",
    title: "进入暗区",
    description: "车辆逐步进入隧道暗区，车道线与路侧锥桶可见，前车保持低速前行。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-09.webp")
  },
  {
    id: "risk-locked",
    time: "09:23:24",
    title: "关键证据锁定",
    description: "系统锁定切入、跟随与制动前后的关键片段，供后续人工复核。",
    image: assetUrl("assets/figma/night-ai/accident-review/frame-10.webp")
  }
] as const;

const accidentReviewImagePreloads = new Map<string, HTMLImageElement>();

type AccidentPanelStage = "safety" | "loading" | "review";

interface NightAccidentGuideOverlayProps {
  callVisible: boolean;
  evaSrc: string;
  onConfirmSafe: () => void;
  onHangupCall: () => void;
}

export function NightAccidentGuideOverlay({
  callVisible,
  evaSrc,
  onConfirmSafe,
  onHangupCall
}: NightAccidentGuideOverlayProps) {
  const [callElapsedSeconds, setCallElapsedSeconds] = useState(0);
  const [panelStage, setPanelStage] = useState<AccidentPanelStage>("safety");

  useEffect(() => {
    const startedAt = Date.now();

    setCallElapsedSeconds(0);
    const intervalId = window.setInterval(() => {
      setCallElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (panelStage !== "loading") {
      return undefined;
    }

    preloadAccidentReviewImages();

    const timeoutId = window.setTimeout(() => {
      setPanelStage("review");
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [panelStage]);

  return (
    <div className="night-accident-guide" aria-label="事故安全引导">
      <div className="night-accident-guide__backdrop" aria-hidden="true" />
      <img
        className="night-accident-guide__eva"
        src={evaSrc}
        alt=""
        draggable={false}
        aria-hidden="true"
      />
      {callVisible ? (
        <section className="night-accident-guide__call" aria-label="紧急联系人通话中">
          <div className="night-accident-guide__call-name">李亚宁</div>
          <div className="night-accident-guide__call-time" aria-live="polite">
            {formatCallDuration(callElapsedSeconds)}
          </div>
          <button
            type="button"
            className="night-accident-guide__hangup"
            aria-label="挂断电话"
            onClick={onHangupCall}
          >
            <Phone size={40} strokeWidth={3.4} />
          </button>
        </section>
      ) : null}
      <section
        className={`night-accident-guide__panel night-accident-guide__panel--${panelStage}`}
        aria-label={panelStage === "review" ? "事故回溯分析" : "确保安全"}
      >
        {panelStage === "safety" ? (
          <SafetyGuidePanel onNext={() => setPanelStage("loading")} />
        ) : null}
        {panelStage === "loading" ? <AccidentReviewLoading /> : null}
        {panelStage === "review" ? <AccidentReviewPanel onClose={onConfirmSafe} /> : null}
      </section>
    </div>
  );
}

function preloadAccidentReviewImages() {
  for (const frame of accidentReviewFrames) {
    if (accidentReviewImagePreloads.has(frame.image)) {
      continue;
    }

    const image = new Image();
    image.decoding = "async";
    image.src = frame.image;
    accidentReviewImagePreloads.set(frame.image, image);
    void image.decode?.().catch(() => undefined);
  }
}

function SafetyGuidePanel({ onNext }: { onNext: () => void }) {
  return (
    <>
      <h2>确保安全</h2>
      <div className="night-accident-guide__steps" aria-label="事故处理步骤">
        {accidentGuideSteps.map((step) => (
          <div className="night-accident-guide__step" key={step.id}>
            <span className="night-accident-guide__step-number">{step.index}</span>
            <span className="night-accident-guide__step-label">{step.label}</span>
          </div>
        ))}
      </div>
      <div className="night-accident-guide__cards">
        {accidentGuideSteps.map((step) => (
          <article className="night-accident-guide__card" key={step.id}>
            <img src={step.image} alt={step.caption} draggable={false} />
            <span>{step.caption}</span>
          </article>
        ))}
      </div>
      <p className="night-accident-guide__disclaimer">
        本引导仅为简易指引，实际处理流程和责任认定以当地法律、行政机关要求及处理结果为准，如因误判时效、缺失关键证据、未及时报警等情况造成的一切后果由您自行承担
      </p>
      <button type="button" className="night-accident-guide__emergency">
        <PhoneCall size={25} strokeWidth={2.8} />
        <span>急救电话 120</span>
      </button>
      <button
        type="button"
        className="night-accident-guide__confirm"
        onClick={onNext}
      >
        我已确认安全，下一步
      </button>
    </>
  );
}

function AccidentReviewLoading() {
  return (
    <div className="night-accident-review-loading" aria-live="polite">
      <div className="night-accident-review-loading__orb" aria-hidden="true">
        <span />
      </div>
      <h2>正在生成事故回溯</h2>
      <p>正在同步行车记录仪、车辆状态和视觉关键帧。</p>
      <div className="night-accident-review-loading__steps" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function AccidentReviewPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className="night-accident-review">
      <div className="night-accident-review__summary">
        <div className="night-accident-review__warning">
          <AlertTriangle size={23} strokeWidth={2.6} />
          <span>当前能见度受限（隧道 / 雨雾），视觉识别结果可能不准确。</span>
        </div>
        <div className="night-accident-review__facts">
          <p><strong>事故时间：</strong>2024年1月15日 09:23:15</p>
          <p><strong>事故地点：</strong>XX隧道入口处</p>
          <p>
            <strong>事故经过：</strong>
            车辆在进入隧道时，前方蓝色车辆与左侧黑色 SUV 在入口处发生挤压；随后白色宝马 X1 从左侧强行切入本车道，本车跟车距离不足（约15m），制动不及时，与切入车辆发生接触。
          </p>
        </div>
        <div className="night-accident-review__note">以下为基于车载传感器和视觉数据的事实还原，不构成任何法律判断。</div>
      </div>
      <div className="night-accident-review__timeline">
        <div>
          <span>事故关键帧</span>
          <small>共 10 帧 · 可用于人工复核</small>
        </div>
      </div>
      <div className="night-accident-review__scroll">
        <div className="night-accident-review__time-row" aria-label="事故时间轴">
          {accidentReviewFrames.map((frame) => (
            <div className="night-accident-review__time-point" key={`${frame.id}-time`}>
              <span aria-hidden="true" />
              <strong>{frame.time}</strong>
              <small>{frame.title}</small>
            </div>
          ))}
        </div>
        <div className="night-accident-review__frames">
          {accidentReviewFrames.map((frame) => (
            <article className="night-accident-review__frame" key={frame.id}>
              <div className="night-accident-review__frame-image">
                <img src={frame.image} alt={frame.title} draggable={false} />
                <span>{frame.time}</span>
                <em><Sparkles size={16} strokeWidth={2.6} />AI分析</em>
              </div>
              <h3>{frame.title}</h3>
              <p>{frame.description}</p>
            </article>
          ))}
        </div>
      </div>
      <div className="night-accident-review__actions" aria-label="事故回溯操作">
        <button
          type="button"
          className="night-accident-review__action night-accident-review__action--primary"
          onClick={onClose}
        >
          同步到手机
        </button>
        <button
          type="button"
          className="night-accident-review__action night-accident-review__action--secondary"
          onClick={onClose}
        >
          暂时收起
        </button>
      </div>
    </div>
  );
}

function formatCallDuration(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
}
